import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// Excel del análisis histórico de una persona: hoja "Resumen" (indicadores) y hoja
// "Detalle por día" (turno, marcaciones con fecha, horas ejecutadas y autorizadas).

const ENCABEZADO = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F4E79' } };
const FALTA = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFDECEC' } };
const FMT_HORAS = '#,##0.00';

const estilarEncabezado = (fila) => {
    fila.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    fila.eachCell((c) => {
        c.fill = ENCABEZADO;
        c.alignment = { vertical: 'middle', wrapText: true };
    });
    fila.height = 28;
};

const fechaDDMM = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const diaSemana = (iso) => DIAS[new Date(`${iso}T12:00:00`).getDay()];

const textoAusencia = (a) =>
    !a
        ? ''
        : a.tipo === 'incapacidad'
          ? `Incapacidad${a.origen ? ` (${a.origen === 'laboral' ? 'laboral' : 'común'})` : ''}`
          : `Permiso${a.remunerado ? '' : ' no remunerado'}`;

const observaciones = (d) => {
    const n = [];
    if (d.ausencia) n.push(d.worked ? `${textoAusencia(d.ausencia)} registrada, pero marcó` : textoAusencia(d.ausencia));
    if (!d.schedule && d.worked) n.push('Sin turno planificado');
    if (d.worked?.records > 1) n.push(`${d.worked.records} marcaciones`);
    if (d.worked?.elapsedHours > 16) n.push(`${Math.round(d.worked.elapsedHours)} h entre llegada y salida`);
    if (d.worked?.endTime && d.worked.authorizedHours == null) n.push('Sin autorizar');
    return n.join(' · ');
};

const nombreArchivo = (s) =>
    s
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^A-Za-z0-9]+/g, '_');

/** `resultado`: respuesta del análisis histórico con `employee` (la fila de la persona). */
export const exportarControlExcel = async (resultado) => {
    const fila = resultado?.employee;
    if (!fila) return;
    const e = fila.employee;
    const st = fila.statistics;
    const nombre = `${e.nombre || ''} ${e.apellido || ''}`.trim();
    const wb = new ExcelJS.Workbook();
    wb.creator = 'OptyLav';

    // ---------------------------------------------------------------- resumen
    const r = wb.addWorksheet('Resumen');
    r.columns = [{ width: 34 }, { width: 22 }, { width: 50 }];
    r.addRow(['Reporte de asistencia y puntualidad']).font = { bold: true, size: 14 };
    r.addRow([nombre]).font = { bold: true, size: 12 };
    r.addRow([`CC ${e.cedula || '—'} · ${e.lavanderia?.nombre || 'Sin centro'} · ${fila.assignment?.role?.nombre || e.rol || ''}`]);
    r.addRow([`Periodo: ${fechaDDMM(resultado.startDate)} al ${fechaDDMM(resultado.endDate)}`]);
    r.addRow([]);
    estilarEncabezado(r.addRow(['Indicador', 'Valor', 'Detalle']));
    const filas = [
        ['Asistencia', st.attendanceRate == null ? '—' : `${st.attendanceRate} %`, `${st.plannedWorkedDays} de ${st.exigibleDays} turnos cumplidos (sin contar incapacidades ni permisos)`],
        ['Puntualidad', st.punctualityRate == null ? '—' : `${st.punctualityRate} %`, `${st.onTimeDays} a tiempo · ${st.lateDays} tarde`],
        ['Minutos tarde', st.lateMinutes, `Promedio ${st.avgDelayMinutes} min por llegada`],
        ['Balance de llegada (min)', st.balanceMinutes, st.balanceMinutes > 0 ? 'En contra (llegadas tarde)' : 'A favor (llegadas antes)'],
        ['Días programados', st.totalDays, ''],
        ['Días trabajados', st.workedDays, `${st.extraDays} sin turno planificado`],
        ['No se presentó', st.absentDays, 'Turnos sin marcación'],
        ['Días de incapacidad', st.incapacityDays, ''],
        ['Días de permiso', st.permissionDays, ''],
        ['Horas ejecutadas (marcadas)', st.executedHours, ''],
        ['Horas autorizadas', st.authorizedHours, `${st.pendingAuthorizationDays} días sin autorizar`],
    ];
    filas.forEach((f) => r.addRow(f));

    // ---------------------------------------------------------------- detalle
    const d = wb.addWorksheet('Detalle por día', { views: [{ state: 'frozen', ySplit: 1 }] });
    d.columns = [
        { header: 'Fecha', key: 'fecha', width: 12 },
        { header: 'Día', key: 'dia', width: 6 },
        { header: 'Turno inicio', key: 'turnoIni', width: 11 },
        { header: 'Turno fin', key: 'turnoFin', width: 10 },
        { header: 'Fecha entrada', key: 'fechaEntrada', width: 13 },
        { header: 'Hora entrada', key: 'horaEntrada', width: 11 },
        { header: 'Llegada (min, + tarde)', key: 'llegada', width: 12 },
        { header: 'Fecha salida', key: 'fechaSalida', width: 13 },
        { header: 'Hora salida', key: 'horaSalida', width: 11 },
        { header: 'Horas entre llegada y salida', key: 'reales', width: 14 },
        { header: 'Horas ejecutadas', key: 'ejecutadas', width: 12 },
        { header: 'Entrada autorizada', key: 'entAut', width: 12 },
        { header: 'Salida autorizada', key: 'salAut', width: 12 },
        { header: 'Horas autorizadas', key: 'autorizadas', width: 12 },
        { header: 'Estado', key: 'estado', width: 16 },
        { header: 'Centro', key: 'centro', width: 22 },
        { header: 'Observación', key: 'obs', width: 46 },
    ];
    estilarEncabezado(d.getRow(1));
    fila.days
        .filter((x) => x.schedule || x.worked || x.ausencia)
        .forEach((x) => {
            const w = x.worked;
            const row = d.addRow({
                fecha: fechaDDMM(x.date),
                dia: diaSemana(x.date),
                turnoIni: x.schedule?.start || '',
                turnoFin: x.schedule?.end || '',
                fechaEntrada: fechaDDMM(w?.startDate),
                horaEntrada: w?.startTime || '',
                llegada: x.delay?.minutes ?? '',
                fechaSalida: fechaDDMM(w?.endDate),
                horaSalida: w?.endTime || '',
                reales: w?.elapsedHours ?? '',
                ejecutadas: w?.endTime ? w.executedHours : '',
                entAut: w?.authorizedStart || '',
                salAut: w?.authorizedEnd || '',
                autorizadas: w?.authorizedHours ?? '',
                estado: x.status === 'Sin Turno' ? 'Sin turno' : x.status,
                centro: w?.center || '',
                obs: observaciones(x),
            });
            ['reales', 'ejecutadas', 'autorizadas'].forEach((k) => (row.getCell(k).numFmt = FMT_HORAS));
            if (x.status === 'No se presentó') row.eachCell((c) => (c.fill = FALTA));
            if ((x.delay?.minutes ?? 0) > 0) row.getCell('llegada').font = { color: { argb: 'FFB91C1C' } };
        });
    const total = d.addRow({ fecha: 'Total', ejecutadas: st.executedHours, autorizadas: st.authorizedHours });
    total.font = { bold: true };
    ['ejecutadas', 'autorizadas'].forEach((k) => (total.getCell(k).numFmt = FMT_HORAS));
    d.autoFilter = { from: 'A1', to: 'Q1' };

    const buf = await wb.xlsx.writeBuffer();
    saveAs(
        new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `control-personal_${nombreArchivo(nombre)}_${resultado.startDate}_a_${resultado.endDate}.xlsx`,
    );
};

/** Excel del análisis por centro: hoja por persona y hoja por día. */
export const exportarCentroExcel = async (resultado) => {
    if (!resultado?.employees) return;
    const t = resultado.totals;
    const wb = new ExcelJS.Workbook();
    wb.creator = 'OptyLav';

    const p = wb.addWorksheet('Por persona', { views: [{ state: 'frozen', ySplit: 5 }] });
    p.addRow([`Asistencia y puntualidad · ${resultado.centro || ''}`]).font = { bold: true, size: 14 };
    p.addRow([`Periodo: ${fechaDDMM(resultado.startDate)} al ${fechaDDMM(resultado.endDate)}`]);
    p.addRow([
        `Asistencia ${t.attendanceRate ?? '—'} % · Puntualidad ${t.punctualityRate ?? '—'} % · ${t.lateMinutes} min tarde · ${t.absentDays} inasistencias · ${t.executedHours} h ejecutadas · ${t.authorizedHours} h autorizadas`,
    ]);
    p.addRow([]);
    const columnas = [
        ['Persona', 32],
        ['Cédula', 14],
        ['Centro en la ficha', 22],
        ['Turnos exigibles', 10],
        ['Turnos cumplidos', 10],
        ['Asistencia %', 10],
        ['Puntualidad %', 11],
        ['Llegadas a tiempo', 10],
        ['Llegadas tarde', 10],
        ['Minutos tarde', 10],
        ['Balance llegada (min)', 11],
        ['No se presentó', 10],
        ['Días incapacidad', 10],
        ['Días permiso', 10],
        ['Días sin turno', 10],
        ['Horas ejecutadas', 11],
        ['Horas autorizadas', 11],
        ['Días sin autorizar', 10],
    ];
    estilarEncabezado(p.addRow(columnas.map((c) => c[0])));
    columnas.forEach((c, i) => (p.getColumn(i + 1).width = c[1]));
    resultado.employees.forEach((f) => {
        const s = f.statistics;
        const row = p.addRow([
            `${f.employee.nombre} ${f.employee.apellido}`.trim(),
            f.employee.cedula || '',
            f.employee.lavanderia?.nombre || '',
            s.exigibleDays,
            s.plannedWorkedDays,
            s.attendanceRate ?? '',
            s.punctualityRate ?? '',
            s.onTimeDays,
            s.lateDays,
            s.lateMinutes,
            s.balanceMinutes,
            s.absentDays,
            s.incapacityDays,
            s.permissionDays,
            s.extraDays,
            s.executedHours,
            s.authorizedHours,
            s.pendingAuthorizationDays,
        ]);
        [16, 17].forEach((c) => (row.getCell(c).numFmt = FMT_HORAS));
        if (s.absentDays) row.getCell(12).fill = FALTA;
    });
    p.autoFilter = { from: { row: 5, column: 1 }, to: { row: 5, column: columnas.length } };

    const d = wb.addWorksheet('Por día', { views: [{ state: 'frozen', ySplit: 1 }] });
    d.columns = [
        { header: 'Fecha', key: 'fecha', width: 12 },
        { header: 'Día', key: 'dia', width: 6 },
        { header: 'Turnos planificados', key: 'scheduled', width: 12 },
        { header: 'Asistieron', key: 'attended', width: 11 },
        { header: 'Llegaron tarde', key: 'late', width: 11 },
        { header: 'Minutos tarde', key: 'lateMinutes', width: 11 },
        { header: 'No se presentaron', key: 'absent', width: 12 },
        { header: 'Incapacidad / permiso', key: 'leave', width: 13 },
        { header: 'Sin turno planificado', key: 'extra', width: 12 },
        { header: 'Horas ejecutadas', key: 'executedHours', width: 12 },
        { header: 'Horas autorizadas', key: 'authorizedHours', width: 12 },
    ];
    estilarEncabezado(d.getRow(1));
    resultado.days.forEach((x) => {
        const row = d.addRow({ ...x, fecha: fechaDDMM(x.date), dia: diaSemana(x.date) });
        ['executedHours', 'authorizedHours'].forEach((k) => (row.getCell(k).numFmt = FMT_HORAS));
        if (x.absent) row.getCell('absent').fill = FALTA;
    });

    const buf = await wb.xlsx.writeBuffer();
    saveAs(
        new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `control-centro_${nombreArchivo(resultado.centro || 'centro')}_${resultado.startDate}_a_${resultado.endDate}.xlsx`,
    );
};
